/**
 * Manual de Contabilidad.
 * Verified behavior, derived from code:
 *  - Rol de consulta + exportación. NO somete (accountant NO está en la lista de
 *    POST /receipts, receiptRoutes 42), NO aprueba, NO edita, y NO asigna Proveedor
 *    Sage (reviewer/admin, receiptController 660-662). SÍ exporta a Sage: el export
 *    por lote (Gastos B / V37.5, POST /accounting/exports/expenses, authorize incluye
 *    accountant) marca los recibos como pagados (is_paid) y exportados (is_exported)
 *    al confirmar. El marcado manual "Pagar/Pagar Todo" fue eliminado.
 *  - VE los datos APROBADOS de Gastos y Nómina (getApprovedReceipts / getApprovedSheets,
 *    authorize ['admin','accountant','reviewer'], accountingRoutes 14-15).
 *  - EXPORTA a Sage 50: Gastos, Nómina e Historial (accountingRoutes 25-27; authorize
 *    incluye accountant). Mismo flujo de exportación que Revisión Final (AccountingPortal).
 *  - Aterriza en /contabilidad ("Sage Report"), 3 pestañas: "Gastos Aprobados",
 *    "Nómina Aprobada", "Exportaciones" (AccountingPortal tabs 13-17).
 *  - NOTA: contra lo descrito en V17 (export solo accountant+admin), el código actual
 *    también da exportación a Revisión Final (reviewer). El accountant sigue siendo
 *    usuario principal de exportación.
 * Contenido derivado de: accountingRoutes, adminController (getApprovedReceipts,
 * getApprovedSheets, exportExpensesCsv, exportNominaCsv, getExportHistory),
 * AccountingPortal (ApprovedExpenses, ApprovedNomina, ExportHistory).
 */
export default {
  roleKey: 'accountant',
  roleLabel: 'Contabilidad',
  icon: '📊',
  subtitle: 'Consulta y exportación a Sage 50 de Gastos y Nómina aprobados.',
  sections: [
    {
      id: 'portal',
      group: 'General',
      title: 'Portal de Contabilidad — vista general',
      blocks: [
        { type: 'paragraph', text: 'Su rol es de consulta y exportación. Al iniciar sesión, aterriza en el módulo "Sage Report" (Contabilidad).' },
        { type: 'paragraph', text: 'El portal tiene tres pestañas: "Gastos Aprobados", "Nómina Aprobada" y "Exportaciones". En las dos primeras consulta los datos ya aprobados y descarga el archivo CSV para Sage 50; en la tercera ve el historial de exportaciones.' },
        { type: 'note', variant: 'warning', text: 'Su rol es de consulta y exportación: usted ve y exporta, pero no puede someter, aprobar ni editar los recibos. Al exportar, los recibos incluidos quedan marcados como pagados y exportados (esa marca es parte de la exportación). Asignar el Proveedor Sage es acción de Revisión Final o Administrador, no de Contabilidad — y un recibo sin Proveedor Sage no se puede exportar.' },
      ],
    },
    {
      id: 'gastos-consultar',
      group: 'Gastos',
      title: 'Gastos aprobados — consultar y filtrar',
      blocks: [
        { type: 'paragraph', text: 'En la pestaña "Gastos Aprobados" usted consulta los recibos ya aprobados. Puede filtrar la lista y expandir cada recibo para ver sus partidas.' },
        { type: 'steps', items: [
          'Abra "Sage Report" → pestaña "Gastos Aprobados".',
          'Use los filtros: rango de fechas (desde / hasta), método de pago, tarjeta de crédito (cuando el método es "Tarjeta de crédito") y proyecto.',
          'Expanda un recibo para revisar sus partidas, códigos (Tarea / Código GL) y montos.',
        ] },
        { type: 'note', variant: 'info', text: 'Esta vista es de solo lectura. La misma lista filtrada es la que se incluye al exportar.' },
      ],
    },
    {
      id: 'gastos-exportar',
      group: 'Gastos',
      title: 'Exportar Gastos a CSV (Sage 50)',
      blocks: [
        { type: 'steps', items: [
          'En "Gastos Aprobados", ajuste los filtros y revise en pantalla la previsualización de los recibos que se incluirán.',
          'Pulse "Exportar". Se abre una ventana de confirmación con el resumen por proyecto (recibos y montos) y la lista de recibos EXCLUIDOS por no tener Proveedor Sage.',
          'Confirme para descargar el CSV de Sage. Al confirmar, los recibos exportados quedan marcados como pagados y exportados en una sola acción; "Cancelar" no cambia nada.',
        ] },
        { type: 'note', variant: 'warning', text: 'IMPORTANTE — duplicados: por defecto la exportación EXCLUYE los recibos que ya fueron exportados antes. Si un recibo no aparece, lo más probable es que ya se haya exportado. Marque la casilla "Incluir exportados" solo cuando necesite volver a exportarlos.' },
        { type: 'note', variant: 'info', text: 'Si ve el mensaje "No hay recibos nuevos para exportar", significa que todo lo que coincide con sus filtros ya se exportó. Marque "Incluir exportados" para re-exportarlos.' },
        { type: 'note', variant: 'info', text: 'El archivo descargado es un CSV con el formato de importación de Sage 50. El equipo de Cuentas por Pagar (AP) lo importa manualmente a Sage (el Código GL corresponde al Account ID y el proyecto al Job ID).' },
      ],
    },
    {
      id: 'nomina-consultar',
      group: 'Nómina',
      title: 'Nómina aprobada — consultar y filtrar',
      blocks: [
        { type: 'paragraph', text: 'En la pestaña "Nómina Aprobada" usted consulta las tarjetas de tiempo ya aprobadas.' },
        { type: 'steps', items: [
          'Abra "Sage Report" → pestaña "Nómina Aprobada".',
          'Use los filtros: rango de fechas y proyecto.',
          'Revise las tarjetas aprobadas y sus horas.',
        ] },
        { type: 'note', variant: 'info', text: 'Esta vista es de solo lectura. La misma lista filtrada es la que se incluye al exportar.' },
      ],
    },
    {
      id: 'nomina-exportar',
      group: 'Nómina',
      title: 'Exportar Nómina a CSV (Sage 50)',
      blocks: [
        { type: 'steps', items: [
          'En "Nómina Aprobada", ajuste los filtros (rango de fechas y proyecto) y revise la previsualización de las tarjetas que se incluirán.',
          'Pulse "Exportar CSV (Sage)" para descargar el archivo.',
        ] },
        { type: 'note', variant: 'warning', text: 'IMPORTANTE — duplicados: igual que en Gastos, por defecto se EXCLUYEN los registros ya exportados. Marque "Incluir exportados" solo si necesita re-exportarlos. El mensaje "No hay registros nuevos para exportar" indica que ya se exportaron.' },
        { type: 'note', variant: 'info', text: 'Las entradas codificadas a Cuentas GL todavía NO se incluyen en el archivo Sage (el mapeo GL→Sage está pendiente de Contabilidad). Si el sistema excluye entradas por esta razón, le mostrará un aviso con la cantidad.' },
      ],
    },
    {
      id: 'historial-export',
      group: 'General',
      title: 'Historial de exportaciones',
      blocks: [
        { type: 'paragraph', text: 'En la pestaña "Exportaciones" usted ve el historial de los archivos que se han exportado, identificados por módulo (Gastos o Nómina) y fecha.' },
        { type: 'note', variant: 'info', text: 'Use el historial para confirmar qué ya se exportó antes de generar un archivo nuevo. Recuerde que, por defecto, las exportaciones excluyen lo ya exportado.' },
      ],
    },
  ],
};
